import React, { useRef, useState } from 'react';
import { Archive, Check, ChevronDown, ChevronUp, Share2, Clock3, Link2, Loader2, MinusCircle, RotateCcw, ShieldCheck, Trash2, TriangleAlert, Upload, UploadCloud, X } from 'lucide-react';
import { ConfidenceTag } from '../ui/ConfidenceTag';
const GMP_NAME = 'Manufacturer quality assurance certificate — pick ONE';
const COA_NAME = 'Certificate of Analysis (finished product)';
const coaValidations: {
  name: string;
  detail: string;
  tone: 'pass' | 'fail';
}[] = [{
  name: 'Document within its validity period',
  detail: 'Valid through 24 January 2028 — not expired.',
  tone: 'pass'
}, {
  name: 'Product identity matches this dossier',
  detail: "This certificate is for 'Chocolate Powdered Drink (Hot or Cold)', not 'Cheese Snack'. Southbank Foods Pty Ltd — the manufacturer named on this document — is the supplier of record for Chocolate Powdered Drink, not for this product. This is the wrong Certificate of Analysis.",
  tone: 'fail'
}, {
  name: 'Issuer recognised',
  detail: 'Southbank Foods Pty Ltd is a recognised supplier in your workspace — registered against a different product than this one.',
  tone: 'pass'
}];
const coaExtracted: {
  field: string;
  value: string;
  level: 'HIGH' | 'MEDIUM';
}[] = [{
  field: 'Document type',
  value: 'Finished Product CoA',
  level: 'HIGH'
}, {
  field: 'Product',
  value: 'Chocolate Powdered Drink (Hot or Cold)',
  level: 'HIGH'
}, {
  field: 'Manufacturer',
  value: 'Southbank Foods Pty Ltd, 12 Harbour Rd, Rhodes NSW 2138, Australia',
  level: 'HIGH'
}, {
  field: 'Batch number',
  value: 'SB-2607-114',
  level: 'HIGH'
}, {
  field: 'Date of manufacture',
  value: '24/07/2026',
  level: 'HIGH'
}, {
  field: 'Expiry / retest date',
  value: '24/01/2028',
  level: 'HIGH'
}, {
  field: 'Issuer',
  value: 'Southbank Foods Pty Ltd',
  level: 'MEDIUM'
}, {
  field: 'Authorised signatory',
  value: 'QA Manager',
  level: 'MEDIUM'
}];
type GmpStage = 'idle' | 'reading' | 'classifying' | 'done';
const gmpValidations = [{
  tone: 'fail' as const,
  name: 'Document within its validity period',
  detail: 'This certificate expired on 20 February 2026, 179 days ago. A manufacturer quality assurance certificate must be current at the time of filing.'
}, {
  tone: 'pass' as const,
  name: 'Certifying body recognized',
  detail: 'AsureQuality is a recognized New Zealand food safety certification body.'
}, {
  tone: 'pass' as const,
  name: 'Manufacturer name matches product record',
  detail: "'DairyWorks NZ Limited' matches the manufacturer declared on the artwork and in the formulation record."
}];
const gmpExtracted = [{
  field: 'Document type',
  value: 'GMP Certificate'
}, {
  field: 'Manufacturer',
  value: 'DairyWorks NZ Limited'
}, {
  field: 'Certifying body',
  value: 'AsureQuality'
}, {
  field: 'Certificate number',
  value: 'GMP-NZ-2023-0447'
}, {
  field: 'Issue date',
  value: '20/02/2023'
}, {
  field: 'Expiry date',
  value: '20/02/2026'
}] as const;
const AO_2014_0029_URL = 'https://www.fda.gov.ph/draft-for-comments-revised-rules-and-regulations-on-the-licensing-of-food-establishments-and-registration-of-processed-food-and-other-food-products-and-for-other-purposes-repealing-administrative/';
const AO_2016_0003_URL = 'https://233773342789-lic.s3.eu-central-1.amazonaws.com/attachments/legislation/philippines/EMB%20Administrative%20Order%20No.0003-2016%20-%20Food%20and%20Drug_Part%201_15.02.2016.pdf';
const FILING_CITATIONS = [{
  text: 'FDA AO - 2014-0029',
  url: AO_2014_0029_URL
}, {
  text: 'Administrative Order No. 2016-0003',
  url: AO_2016_0003_URL
}];
const CFRR = 'FDA AO - 2014-0029';
const AO_URL = 'https://www.fda.gov.ph/wp-content/uploads/2021/03/Administrative-Order-No.-2014-0030.pdf';
type Citation = {
  text: string;
  url?: string;
};
type Requirement = {
  name: string;
  provided: string;
  why: string;
  greenNote?: string;
  citations: Citation[];
};
type Excluded = {
  name: string;
  reason: string;
  citation?: Citation;
};
const feeRows: {
  label: string;
  value: string;
  tag: string;
  tone: 'amber' | 'red' | 'green';
  note?: string;
}[] = [{
  label: 'LTO, Importer/Trader — initial (2-yr)',
  value: '~PHP 8,000',
  tag: 'Moderate confidence',
  tone: 'amber',
  note: 'Reconfirm current schedule.'
}, {
  label: 'LTO, Importer/Trader — renewal (5-yr)',
  value: '~PHP 20,000',
  tag: 'Moderate confidence',
  tone: 'amber',
  note: 'Reconfirm current schedule.'
}, {
  label: 'CPR processing time',
  value: '~114 calendar days from complete submission',
  tag: 'Stated FDA figure',
  tone: 'green'
}, {
  label: 'CPR fee',
  value: 'Unresolved',
  tag: 'Low confidence',
  tone: 'red',
  note: 'AO 2024-0016 attempted a 1,400% hike (PHP 200 → 3,000 for low-risk), suspended by FDA Advisory 2025-0076 (24 Jan 2025) after industry pushback. Confirm the current live figure directly with the FDA before quoting a client.'
}];
const requirements: Requirement[] = [{
  name: 'Valid LTO (Importer/Trader)',
  provided: 'Current License to Operate showing Importer or Trader authorisation.',
  why: 'Precondition for everything else in the filing sequence — nothing else can be submitted without it.',
  citations: [{
    text: CFRR
  }]
}, {
  name: 'Compliant labels/artwork, all packaging sizes',
  provided: 'Finalised label artwork for every packaging size sold, meeting every AO 2014-0030 mandatory element.',
  why: "The artwork itself is a filing document — it can't be submitted until the Packaging & Label findings are resolved.",
  citations: [{
    text: 'AO 2014-0030',
    url: AO_URL
  }]
}, {
  name: 'Product photos, all angles, 2+ perspectives',
  provided: 'Photographs of the finished product from at least two angles.',
  why: "Confirms visual match between the physical product and what's registered.",
  citations: [{
    text: CFRR
  }]
}, {
  name: 'Full ingredient list + quantitative declaration (QUID)',
  provided: 'Every ingredient with its percentage, matching the label exactly.',
  why: 'Required under AO 2014-0030 §VI.A.3.e; the Formula tab confirms the current declaration is already compliant — this is a copy-over, not new work.',
  greenNote: 'Already satisfied on current artwork — confirm and attach.',
  citations: [{
    text: 'AO 2014-0030 §VI.A.3.e',
    url: AO_URL
  }]
}, {
  name: 'Claim-substantiation documents',
  provided: 'Evidence scoped to each claim actually made on the label — not a blanket dossier. Covers 8g protein, source of calcium, halal, and the rest.',
  why: "Two claims (HALAL, 27% MORE PROTEIN) currently fail on the Claims tab and can't be substantiated as-is — resolve those first.",
  citations: [{
    text: CFRR
  }]
}, {
  name: 'Certificate of Analysis (finished product)',
  provided: 'Finished-product test report - Compositional COA, Microbiological Test, Shelf-Life Stability Data. Must be issued within the last 24 months.',
  why: 'Likely required given the Medium Risk tier.',
  citations: [{
    text: 'FDA Circular No.2022-012',
    url: 'https://www.fda.gov.ph/fda-circular-no-2022-012-guidelines-on-the-microbiological-requirements-and-assessment-of-certain-prepackaged-processed-food-products-repealing-fda-circular-no-2013-010-entitled-revised-g/'
  }]
}, {
  name: 'Manufacturer quality assurance certificate — pick ONE',
  provided: 'One of: GMP Certificate, SPS Certificate, Health Certificate, ISO 22000, FSSC, HACCP, or Certificate of Free Sale. Only one is required, not all.',
  why: "Confirms the manufacturer's quality assurance basis.",
  citations: [{
    text: CFRR
  }]
}, {
  name: 'Right-to-import agreement — pick ONE',
  provided: 'One of: Foreign Agency Agreement, Distributorship Certificate, Appointment Letter, Proforma Invoice, or MOA. Only one is required, not all.',
  why: 'Establishes the right to import and register this brand in the Philippines.',
  citations: FILING_CITATIONS
}];
const excluded: Excluded[] = [{
  name: 'Commercial sample (physical)',
  reason: "Food-supplement-only requirement — doesn't apply to conventional food.",
  citation: {
    text: 'Administrative Order No. 2016-0003',
    url: AO_2016_0003_URL
  }
}, {
  name: 'Stability/shelf-life study',
  reason: 'Food-supplement-only requirement. A BEST BEFORE date still needs setting — just not via a mandatory filed study.',
  citation: {
    text: CFRR
  }
}, {
  name: 'Safety/toxicity data (e.g. LD-50)',
  reason: 'Food-supplement-only requirement, specific to herbs and botanicals.',
  citation: {
    text: CFRR
  }
}, {
  name: 'Claim dossier mapped to an ASEAN-permitted supplement category',
  reason: "Wrong framework — that's ASEAN Health Supplement, not conventional food."
}, {
  name: 'BAI/NMIS Sanitary-Phytosanitary Import Clearance',
  reason: 'FDA absorbed regulation of processed animal-origin food from BAI in 2017 — this only applies to unprocessed dairy imports, not a processed composite like this product.',
  citation: {
    text: 'ChemLinked PH Dairy Regulation'
  }
}];
export function PhDossierTab({
  onScanLabel,
  onGmpDetected,
  onCoaWrongProduct
}: {
  onScanLabel: () => void;
  onGmpDetected?: () => void;
  onCoaWrongProduct?: (active: boolean) => void;
}) {
  const [requiredOpen, setRequiredOpen] = useState(true);
  const [excludedOpen, setExcludedOpen] = useState(false);
  const [gmpStage, setGmpStage] = useState<GmpStage>('idle');
  const [gmpSource, setGmpSource] = useState<'upload' | 'reuse'>('upload');
  const [reuseOpen, setReuseOpen] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [coaStage, setCoaStage] = useState<GmpStage>('idle');
  const [coaToast, setCoaToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coaInputRef = useRef<HTMLInputElement>(null);
  const gmpDone = gmpStage === 'done';
  const showCoaToast = (message: string) => {
    setCoaToast(message);
    window.setTimeout(() => setCoaToast(null), 5000);
  };
  const handleCoaFile = () => {
    setCoaStage('reading');
    window.setTimeout(() => setCoaStage('classifying'), 1500);
    window.setTimeout(() => {
      setCoaStage('done');
      onCoaWrongProduct?.(true);
      showCoaToast('Finished Product CoA — wrong product detected.');
    }, 3000);
  };
  const handleCoaMove = () => {
    setCoaStage('idle');
    onCoaWrongProduct?.(false);
    showCoaToast("Moved to Chocolate Powdered Drink's Product Dossier.");
  };
  const handleCoaDelete = () => {
    setCoaStage('idle');
    onCoaWrongProduct?.(false);
  };
  const finish = () => {
    onGmpDetected?.();
    setToastVisible(true);
    window.setTimeout(() => setToastVisible(false), 5000);
  };
  const handleFile = () => {
    setGmpSource('upload');
    setGmpStage('reading');
    window.setTimeout(() => setGmpStage('classifying'), 1500);
    window.setTimeout(() => {
      setGmpStage('done');
      finish();
    }, 3000);
  };
  const handleReuse = () => {
    setGmpSource('reuse');
    setReuseOpen(false);
    setGmpStage('done');
    finish();
  };
  return <div className="space-y-6" data-id="element-1569" data-mp="23c3vbi6cd6un">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1570" data-mp="23bc9gz2jqnhk">
        <div data-id="element-1571" data-mp="1zb1uiwws0mqk">
          <h2 className="font-[Figtree] text-2xl font-semibold tracking-tight text-evergreen" data-id="element-1572" data-mp="1zbjzqj1f2bam">Cheese Snack</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#6d7e71]" data-id="element-1573" data-mp="2w1kio1f3txk0">
            CPR filing dossier for the Philippines — Medium Risk, imported dairy composite.
          </p>
        </div>
        <button type="button" onClick={onScanLabel} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-lime-glow px-4 py-3 text-sm font-semibold text-[#182d22] shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-forest-green focus:ring-offset-2" data-id="element-1574" data-mp="2m7k8vm8vo44y">
          <UploadCloud className="h-4 w-4" data-id="element-1575" data-mp="18309h8hfhllv" />
          Scan a label
        </button>
      </div>

      <section className="max-w-2xl rounded-2xl border-2 border-forest-green bg-[#f2fbf4] p-5 shadow-sm" aria-label="Selected market" data-id="element-1576" data-mp="1d00gblm3qse1">
        <div className="flex items-start justify-between gap-4" data-id="element-1577" data-mp="ybvx8gj50euq">
          <div data-id="element-1578" data-mp="13a76oq5isncf">
            <p className="flex items-center gap-2 font-[Figtree] text-lg font-semibold text-forest-green" data-id="element-1579" data-mp="139nxpyxul9aj">
              <span className="text-xl" aria-hidden="true" data-id="element-1580" data-mp="pa64969lx7uu">
                🇵🇭
              </span>
              Philippines
            </p>
            <p className="mt-3 text-sm text-[#5f7567]" data-id="element-1581" data-mp="fdjm4p8o0uwi">Conventional processed food — CPR required (AO 2014-0029)</p>
          </div>
          <span className="rounded-full bg-amber-100 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-800" data-id="element-1582" data-mp="kdfokk1pse9y">
            Medium risk
          </span>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#e1ece0]" aria-label="0% complete" data-id="element-1583" data-mp="2epciqkrjjy8p">
          <div className="h-full w-0 rounded-full bg-forest-green" data-id="element-1584" data-mp="33txd6cs30ig" />
        </div>
        <div className="mt-3 flex items-center justify-between font-mono text-xs font-semibold" data-id="element-1585" data-mp="3ua4tqp6ufd3g">
          <span className="text-[#6b7d70]" data-id="element-1586" data-mp="ct52khwjnl12">Required 0/8</span>
          <span className="text-red-700" data-id="element-1587" data-mp="cuonwmx24m9p">8 missing</span>
        </div>
      </section>

      <section aria-labelledby="ph-required-heading" data-id="element-1588" data-mp="8029eavoidle">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1589" data-mp="lsifjkjvmrqj">
          <h2 id="ph-required-heading" className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1590" data-mp="hsq1isbprtds">
            Required documents · Philippines
          </h2>
          <div className="flex flex-wrap gap-2" data-id="element-1591" data-mp="qsmxzg4chwje">
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-1592" data-mp="qty0zdpyv09g">
              <Share2 className="h-4 w-4" data-id="element-1593" data-mp="2smcfe2taxml4" />
              Share documents
            </button>
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-1594" data-mp="2u39jhvghunv8">
              <Link2 className="h-4 w-4" data-id="element-1595" data-mp="38ypf44wq2rtq" />
              Request documents
            </button>
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-800 transition hover:bg-red-50" data-id="element-1596" data-mp="2bb75krv8lloo">
              <Trash2 className="h-4 w-4" data-id="element-1597" data-mp="2l7a8w11trqd7" />
              Delete market dossier
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-[#dce3da] bg-white p-6 shadow-sm" data-id="element-1598" data-mp="2q1eh0nmtu4fv">
          <div className="flex items-center justify-between gap-4" data-id="element-1599" data-mp="1si3cb7lxlaul">
            <span className="flex items-center gap-2 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1600" data-mp="1xhzfj4mt0y93">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#eff3ed] text-[#6e8173]" data-id="element-1601" data-mp="enmihqk3rdsp">
                <Archive className="h-5 w-5" data-id="element-1602" data-mp="ancfmmrlx3i2" />
              </span>
              Documents
            </span>
            <span className="font-mono text-sm font-semibold text-[#6d7e71]" data-id="element-1603" data-mp="5pkinec3ur70">Uploaded {gmpDone ? '1' : '0'}/8</span>
          </div>
          <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-[#edf1eb]" aria-label="0% documents complete" data-id="element-1604" data-mp="3rv7r4kd8n320">
            <div className="h-full w-0 rounded-full bg-[#76867c]" data-id="element-1605" data-mp="3n3psw42lngkq" />
          </div>
          <div className="mt-5 grid gap-6 md:grid-cols-2" data-id="element-1606" data-mp="3j1up8sx74r3s">
            <div className="space-y-2 font-mono text-sm text-[#607266]" data-id="element-1607" data-mp="3j09x19yctk93">
              <StatLine label="Uploaded" value={gmpDone ? '1' : '0'} data-id="element-1608" data-mp="3e4kv4zyv3r9m" />
              <StatLine label="Expiring soon" value="0" data-id="element-1609" data-mp="394gacxqx8adl" />
              <StatLine label="Needs review" value="0" data-id="element-1610" data-mp="39614wnd8jtbg" />
            </div>
            <div className="space-y-2 font-mono text-sm text-[#607266]" data-id="element-1611" data-mp="345n67vxixvq3">
              <StatLine label="Missing" value="8" dot="bg-red-600" data-id="element-1612" data-mp="2ugg911msaziq" />
              <StatLine label="Expired" value={gmpDone ? '1' : '0'} dot={gmpDone ? 'bg-red-600' : undefined} data-id="element-1613" data-mp="2zgazx56k7p3s" />
              <StatLine label="Incomplete" value="0" data-id="element-1614" data-mp="2lhbahvb8366k" />
            </div>
          </div>
          {gmpDone && <p className="mt-4 text-xs text-[#8b968d]" data-id="element-1615" data-mp="2phmwbbfbncjm">
              Expired documents do not count toward completeness — &apos;Required 0/8&apos; is unchanged.
            </p>}
          <p className="mt-5 font-mono text-xs font-semibold text-[#718174]" data-id="element-1616" data-mp="3i7lnbcd2tjqx">0% complete</p>
        </div>
      </section>

      <div className="space-y-4" data-id="element-1617" data-mp="34em6n71k5jhs">
        <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-1618" data-mp="34dkwd4sd9114">
          <button type="button" onClick={() => setRequiredOpen(open => !open)} aria-expanded={requiredOpen} className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-[#f8faf7] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest-green" data-id="element-1619" data-mp="38f64ydxvat5i">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-[#6f8174]" data-id="element-1620" data-mp="1kigfivzq839v">
              <ShieldCheck className="h-5 w-5" data-id="element-1621" data-mp="1z7meqjykdthr" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-1622" data-mp="1uachii4xodjd">
              <span className="block font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1623" data-mp="1ube2pffnyget">Required for filing</span>
              <span className="mt-0.5 block font-mono text-xs text-[#718174]" data-id="element-1624" data-mp="11knho83ymjjc">8 missing</span>
            </span>
            {requiredOpen ? <ChevronUp className="h-5 w-5 text-[#718174]" data-id="element-1625" data-mp="1gi6bhovhvmxn" /> : <ChevronDown className="h-5 w-5 text-[#718174]" data-id="element-1626" data-mp="1gftouyeskoe9" />}
          </button>
          {requiredOpen && <div className="border-t border-[#e5ebe3] px-5" data-id="element-1627" data-mp="2rx1pr9q8bmdo">
              {requirements.map(requirement => {
            if (requirement.name === COA_NAME && coaStage !== 'idle') {
              if (coaStage === 'done') return <CoaResultRow key={requirement.name} requirement={requirement} onMove={handleCoaMove} onDelete={handleCoaDelete} data-id="element-1628" data-mp="kd4mpw6rrv50" />;
              return <ProcessingRow key={requirement.name} title="Finished Product CoA" stage={coaStage} data-id="element-1629" data-mp="z0zhdi4c89kq" />;
            }
            if (requirement.name === GMP_NAME && gmpStage !== 'idle') {
              if (gmpStage === 'done') return <GmpResultRow key={requirement.name} requirement={requirement} reused={gmpSource === 'reuse'} data-id="element-1630" data-mp="182yz6cn6fhoq" />;
              return <GmpProcessingRow key={requirement.name} stage={gmpStage} data-id="element-1631" data-mp="1hz1xf7ewqw6e" />;
            }
            return <RequirementRow key={requirement.name} requirement={requirement} onUpload={requirement.name === GMP_NAME ? () => fileInputRef.current?.click() : requirement.name === COA_NAME ? () => coaInputRef.current?.click() : undefined} onReuse={requirement.name === GMP_NAME ? () => setReuseOpen(true) : undefined} data-id="element-1632" data-mp="3ervd9n6zjc5v" />;
          })}
            </div>}
        </section>

        <section className="overflow-hidden rounded-2xl border border-dashed border-[#d3dbd2] bg-[#fafbf9] shadow-none" data-id="element-1633" data-mp="3v509r3fqol6c">
          <button type="button" onClick={() => setExcludedOpen(open => !open)} aria-expanded={excludedOpen} className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-[#f4f6f2] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest-green" data-id="element-1634" data-mp="3q4ufdfi34kkk">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eef1ec] text-[#96a298]" data-id="element-1635" data-mp="24skn01owg8fe">
              <MinusCircle className="h-5 w-5" data-id="element-1636" data-mp="14ny4r4fdnzu6" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-1637" data-mp="zu3lu37ljow5">
              <span className="block font-[Figtree] text-base font-semibold text-[#7d8c80]" data-id="element-1638" data-mp="ztbqmthwvt6e">Not required for this product</span>
              <span className="mt-0.5 block font-mono text-xs text-[#96a298]" data-id="element-1639" data-mp="pxh2jiux55w8">5 items · deliberately excluded</span>
            </span>
            {excludedOpen ? <ChevronUp className="h-5 w-5 text-[#96a298]" data-id="element-1640" data-mp="1njxyajo07bfv" /> : <ChevronDown className="h-5 w-5 text-[#96a298]" data-id="element-1641" data-mp="1il4i2jhc16c0" />}
          </button>
          {excludedOpen && <ul className="divide-y divide-[#e8ebe6] border-t border-dashed border-[#dde3dc] px-5" data-id="element-1642" data-mp="1dli7khsw246w">
              {excluded.map(item => <li key={item.name} className="flex gap-3 py-4" data-id="element-1643" data-mp="19n0y3rlzwhmb">
                  <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#adb8ae]" aria-hidden="true" data-id="element-1644" data-mp="2860ly1u15gi" />
                  <div className="min-w-0" data-id="element-1645" data-mp="3uae025vssj8t">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-1646" data-mp="29t4ryhrbjfou">
                      <p className="font-[Figtree] text-sm font-semibold text-[#7d8c80]" data-id="element-1647" data-mp="29uzcvw2lb7sx">{item.name}</p>
                      <span className="rounded-full border border-[#d3dbd2] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#8b968d]" data-id="element-1648" data-mp="2dsnbb93hripk">
                        Not required
                      </span>
                    </div>
                    <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-[#8b968d]" data-id="element-1649" data-mp="2siktn8eqpe6u">{item.reason}</p>
                  </div>
                </li>)}
            </ul>}
        </section>
      </div>

      <input ref={fileInputRef} type="file" className="hidden" onChange={event => {
      if (event.target.files?.length) handleFile();
      event.target.value = '';
    }} data-id="element-1650" data-mp="31ku1trmjcvh2" />

      <input ref={coaInputRef} type="file" className="hidden" onChange={event => {
      if (event.target.files?.length) handleCoaFile();
      event.target.value = '';
    }} data-id="element-1651" data-mp="178nsyzh46qrx" />

      {reuseOpen && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="reuse-heading" data-id="element-1652" data-mp="2ai8jgs1fvyws">
          <div className="w-full max-w-lg rounded-2xl border border-[#dce3da] bg-white p-6 shadow-2xl" data-id="element-1653" data-mp="2jhvg70j84gji">
            <div className="flex items-start justify-between gap-4" data-id="element-1654" data-mp="2yew8b55budii">
              <div data-id="element-1655" data-mp="2yd1ibcf77v7k">
                <h2 id="reuse-heading" className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1656" data-mp="2tg2rb2qu04dl">
                  Reuse a certificate
                </h2>
                <p className="mt-1 text-sm text-[#718174]" data-id="element-1657" data-mp="376odb0bl0ufp">Certificates in your Policies library matching this document type.</p>
              </div>
              <button type="button" onClick={() => setReuseOpen(false)} aria-label="Close" className="rounded-lg p-2 text-[#718174] transition hover:bg-[#edf2ec]" data-id="element-1658" data-mp="3h54575ifo7q9">
                <X className="h-4 w-4" data-id="element-1659" data-mp="3vsvrb32z616r" />
              </button>
            </div>
            <button type="button" onClick={handleReuse} className="mt-5 flex w-full items-start gap-3 rounded-xl border border-[#d6dfd3] bg-white p-4 text-left transition hover:border-forest-green/50 hover:bg-[#f7faf5]" data-id="element-1660" data-mp="4l03w4lt0nsn">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#819083]" data-id="element-1661" data-mp="3d1bb70k13la8" />
              <span className="min-w-0 flex-1" data-id="element-1662" data-mp="3hsl1m3deisn6">
                <span className="flex flex-wrap items-center gap-2" data-id="element-1663" data-mp="3htcw1av9ikbv">
                  <span className="font-medium text-evergreen" data-id="element-1664" data-mp="3346xzq85v05k">GMP Certificate — DairyWorks NZ Limited.pdf</span>
                  <span className="rounded-full border border-red-300 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-red-700" data-id="element-1665" data-mp="383tdk6bgoshj">
                    Expired
                  </span>
                </span>
                <span className="mt-1 block text-sm text-[#8b968d]" data-id="element-1666" data-mp="2y3soe3qwvi5f">Linked to 2 products</span>
              </span>
            </button>
          </div>
        </div>}

      {toastVisible && <div role="status" className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl border border-[#dce3da] bg-white px-4 py-3 shadow-xl" data-id="element-1667" data-mp="2m32s4g6yur5n">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-700" data-id="element-1668" data-mp="2cd9hpiik8i2z">
            <TriangleAlert className="h-3.5 w-3.5" data-id="element-1669" data-mp="28bxoz44e3gqk" />
          </span>
          <p className="text-sm font-semibold text-evergreen" data-id="element-1670" data-mp="23c2zn509iz7m">Manufacturer QA certificate — expired on detection.</p>
        </div>}

      {coaToast && <div role="status" className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl border border-[#dce3da] bg-white px-4 py-3 shadow-xl" data-id="element-1671" data-mp="1ycyq2d9fezgr">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-700" data-id="element-1672" data-mp="3sqzoix12dkgb">
            <TriangleAlert className="h-3.5 w-3.5" data-id="element-1673" data-mp="3nsps6j3lsdt4" />
          </span>
          <p className="text-sm font-semibold text-evergreen" data-id="element-1674" data-mp="3j3r8lctyzz">{coaToast}</p>
        </div>}
    </div>;
}
function GmpProcessingRow({
  stage
}: {
  stage: 'reading' | 'classifying';
}) {
  return <ProcessingRow title="Manufacturer quality assurance certificate" stage={stage} data-id="element-1675" data-mp="8vl0avsayv4z" />;
}
function ProcessingRow({
  title,
  stage
}: {
  title: string;
  stage: 'reading' | 'classifying';
}) {
  return <article className="flex items-center gap-3 border-b border-[#edf0eb] py-5 last:border-b-0" data-id="element-1676" data-mp="3d9t01e6zb86k">
      <Loader2 className="h-5 w-5 shrink-0 animate-spin text-amber-500" data-id="element-1677" data-mp="3hbejjie0qkpq" />
      <div data-id="element-1678" data-mp="3hbo7l0fi6hss">
        <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1679" data-mp="3hd7qyzwgh8ys">{title}</h3>
        <p className="mt-1 text-sm text-amber-800" data-id="element-1680" data-mp="3mckd1ifi6c0b">{stage === 'reading' ? 'Reading document…' : 'Classifying and extracting…'}</p>
      </div>
    </article>;
}
function CoaResultRow({
  requirement,
  onMove,
  onDelete
}: {
  requirement: Requirement;
  onMove: () => void;
  onDelete: () => void;
}) {
  const [showValidations, setShowValidations] = useState(true);
  const [showExtracted, setShowExtracted] = useState(false);
  return <article className="flex flex-col gap-3 border-b border-[#edf0eb] py-5 last:border-b-0 lg:flex-row lg:items-start" data-id="element-1681" data-mp="bwmjax9mc4cg">
      <div className="flex min-w-0 flex-1 gap-3" data-id="element-1682" data-mp="qscbxe9b7fq5">
        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-white" data-id="element-1683" data-mp="qr1678wazxwe">
          <X className="h-3 w-3" data-id="element-1684" data-mp="3u9ug9bpvi4lt" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-1685" data-mp="3ualzro674bvq">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1686" data-mp="3pb0z99bdnvgw">
            <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1687" data-mp="3pcubr58o99wt">Finished Product CoA</h3>
            <span className="rounded-full border border-red-300 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-red-700" data-id="element-1688" data-mp="7wm6ti62y381">
              Wrong product
            </span>
          </div>
          <div className="mt-3 max-w-2xl" data-id="element-1689" data-mp="3bh9uee8lczri">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#819083]" data-id="element-1690" data-mp="36jgntk3dm3qp">What to provide</p>
            <p className="mt-1 text-sm leading-relaxed text-[#687a6d]" data-id="element-1691" data-mp="1m0w9zqlw5icz">{requirement.provided}</p>
          </div>
          <p className="mt-3 max-w-2xl font-mono text-xs leading-relaxed text-[#718174]" data-id="element-1692" data-mp="1qvabmar5clq3">
            Uploaded: 19/08/2026 · Product on document:{' '}
            <span className="font-semibold text-red-700" data-id="element-1693" data-mp="1h2mipr3af1wn">Chocolate Powdered Drink (Hot or Cold)</span> · Manufacturer: Southbank Foods Pty Ltd, NSW,
            Australia
          </p>

          <div className="mt-4 max-w-2xl overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-1694" data-mp="29slavvcs3f5j">
            <button type="button" onClick={() => setShowValidations(open => !open)} aria-expanded={showValidations} className="flex w-full items-center gap-2 bg-[#f6f9f4] px-4 py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6f8174]" data-id="element-1695" data-mp="1vsk82acydxnf">
              <ChevronDown className={`h-3.5 w-3.5 transition ${showValidations ? 'rotate-180' : ''}`} data-id="element-1696" data-mp="oga48iixpv4a" />
              Validations
            </button>
            {showValidations && <ul className="divide-y divide-[#edf0eb]" data-id="element-1697" data-mp="1b2fdvx0iabaw">
                {coaValidations.map(validation => <li key={validation.name} className="flex gap-3 px-4 py-3" data-id="element-1698" data-mp="172574i4w3guz">
                    {validation.tone === 'fail' ? <X className="mt-0.5 h-4 w-4 shrink-0 text-red-600" data-id="element-1699" data-mp="1ysrv5yhn5ke5" /> : <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-green" data-id="element-1700" data-mp="1ur6a3puydzsv" />}
                    <div className="min-w-0" data-id="element-1701" data-mp="1q07s8lubgtni">
                      <p className="text-sm font-semibold text-evergreen" data-id="element-1702" data-mp="1pz6da6a6jn0i">{validation.name}</p>
                      <p className="mt-1 text-sm leading-relaxed text-[#617366]" data-id="element-1703" data-mp="1l0lgkgu4zlgt">{validation.detail}</p>
                    </div>
                  </li>)}
              </ul>}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3" data-id="element-1704" data-mp="2dor0509gf4f2">
            <button type="button" onClick={onMove} className="inline-flex items-center gap-2 rounded-xl border border-[#d6dfd3] bg-white px-4 py-2.5 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-1705" data-mp="2l28dngu9lpr8">
              Move to Chocolate Powdered Drink →
            </button>
            <button type="button" onClick={onDelete} className="text-sm font-semibold text-[#637667] transition hover:text-red-700 hover:underline" data-id="element-1706" data-mp="2yyvc5o1v8nz9">
              Delete and upload the correct certificate
            </button>
          </div>

          <div className="mt-3 max-w-2xl overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-1707" data-mp="3drytbubjozpg">
            <button type="button" onClick={() => setShowExtracted(open => !open)} aria-expanded={showExtracted} className="flex w-full items-center gap-2 bg-[#f6f9f4] px-4 py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6f8174]" data-id="element-1708" data-mp="3io5t15dcleyy">
              <ChevronDown className={`h-3.5 w-3.5 transition ${showExtracted ? 'rotate-180' : ''}`} data-id="element-1709" data-mp="12bhze6s1o14w" />
              Extracted data
            </button>
            {showExtracted && <ul className="divide-y divide-[#edf0eb]" data-id="element-1710" data-mp="taab8hj8sgoj">
                {coaExtracted.map(row => <li key={row.field} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5" data-id="element-1711" data-mp="1h04rl92rsndj">
                    <span className="min-w-[10rem] font-mono text-[11px] font-semibold uppercase tracking-[0.11em] text-[#7a897d]" data-id="element-1712" data-mp="1lzhc3n665icy">{row.field}</span>
                    <span className="flex-1 text-sm text-evergreen" data-id="element-1713" data-mp="179ta0jq5yfo6">{row.value}</span>
                    <ConfidenceTag level={row.level} data-id="element-1714" data-mp="1c7lcu8scjm75" />
                  </li>)}
              </ul>}
          </div>
        </div>
      </div>
      <div className="shrink-0 lg:pl-4" data-id="element-1715" data-mp="24pz41pj6g9x9">
        <div className="flex flex-wrap justify-end gap-2" data-id="element-1716" data-mp="24r1u5v8oe8c4">
          <ResultAction icon={<Link2 className="h-3.5 w-3.5" data-id="element-1718" data-mp="3pekzu86a7voa" />} onClick={onMove} data-id="element-1717" data-mp="3pf2skutqjrrg">
            Move to Chocolate Powdered Drink
          </ResultAction>
          <ResultAction destructive icon={<Trash2 className="h-3.5 w-3.5" data-id="element-1720" data-mp="22jz4t0jliry" />} onClick={onDelete} data-id="element-1719" data-mp="3keoti2a43s5k">
            Delete
          </ResultAction>
          <ResultAction icon={<div className="h-3.5 w-3.5" data-id="element-1722" data-mp="3u7nktrwztwsv" />} data-id="element-1721" data-mp="3u62rg5mf0jwl">Document history</ResultAction>
        </div>
      </div>
    </article>;
}
function GmpResultRow({
  requirement,
  reused
}: {
  requirement: Requirement;
  reused: boolean;
}) {
  const [showValidations, setShowValidations] = useState(true);
  const [showExtracted, setShowExtracted] = useState(false);
  return <article className="flex flex-col gap-3 border-b border-[#edf0eb] py-5 last:border-b-0 lg:flex-row lg:items-start" data-id="element-1723" data-mp="3ffbzmxktpkth">
      <div className="flex min-w-0 flex-1 gap-3" data-id="element-1724" data-mp="ur7hogtwpu1t">
        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-white" data-id="element-1725" data-mp="upmjmh8dvsz7">
          <X className="h-3 w-3" data-id="element-1726" data-mp="puqqtevbp1l1" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-1727" data-mp="3jjaxskcczqwg">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1728" data-mp="3jmgou54jt2ut">
            <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1729" data-mp="3oie6cerjsf96">Manufacturer quality assurance certificate</h3>
            <span className="rounded-full border border-red-300 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-red-700" data-id="element-1730" data-mp="3sltwcs943hcw">
              Expired
            </span>
          </div>
          <div className="mt-3 max-w-2xl" data-id="element-1731" data-mp="30rrzgqta510r">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#819083]" data-id="element-1732" data-mp="34uolkri13f9g">What to provide</p>
            <p className="mt-1 text-sm leading-relaxed text-[#687a6d]" data-id="element-1733" data-mp="39sztuxsodhpk">{requirement.provided}</p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1734" data-mp="3emkihz8dozc3">
            <p className="font-mono text-xs text-[#718174]" data-id="element-1735" data-mp="2h4u94b1mfspq">
              Uploaded: 18/08/2026 · Certifying body: AsureQuality · Expires: 20/02/2026{' '}
              <span className="font-semibold text-red-700" data-id="element-1736" data-mp="2m2m2l9hwzmw1">(−179 days)</span>
            </p>
            {reused && <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-800" data-id="element-1737" data-mp="29mmd4sxlsslk">
                <RotateCcw className="h-3 w-3" data-id="element-1738" data-mp="20m7opkpcexdu" />
                Reused from Policies
              </span>}
          </div>

          <div className="mt-4 max-w-2xl overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-1739" data-mp="1vo64uxk3eu1b">
            <button type="button" onClick={() => setShowValidations(open => !open)} aria-expanded={showValidations} className="flex w-full items-center gap-2 bg-[#f6f9f4] px-4 py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6f8174]" data-id="element-1740" data-mp="1qv3awobelikl">
              <ChevronDown className={`h-3.5 w-3.5 transition ${showValidations ? 'rotate-180' : ''}`} data-id="element-1741" data-mp="17x0peji2e3rj" />
              Validations
            </button>
            {showValidations && <ul className="divide-y divide-[#edf0eb]" data-id="element-1742" data-mp="381mfeer766kw">
                {gmpValidations.map(validation => <li key={validation.name} className="flex gap-3 px-4 py-3" data-id="element-1743" data-mp="2y9gol8colv7a">
                    {validation.tone === 'fail' ? <X className="mt-0.5 h-4 w-4 shrink-0 text-red-600" data-id="element-1744" data-mp="337i7nta3xuir" /> : <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-green" data-id="element-1745" data-mp="2oa7yiqkxt0q7" />}
                    <div className="min-w-0" data-id="element-1746" data-mp="2tb5fizu7noq4">
                      <p className="text-sm font-semibold text-evergreen" data-id="element-1747" data-mp="2t7zsrr920yxk">{validation.name}</p>
                      <p className="mt-1 text-sm leading-relaxed text-[#617366]" data-id="element-1748" data-mp="2fcxp7arojlnx">{validation.detail}</p>
                    </div>
                  </li>)}
              </ul>}
          </div>

          <div className="mt-3 max-w-2xl overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-1749" data-mp="ii181et01q4f">
            <button type="button" onClick={() => setShowExtracted(open => !open)} aria-expanded={showExtracted} className="flex w-full items-center gap-2 bg-[#f6f9f4] px-4 py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6f8174]" data-id="element-1750" data-mp="sm9amf6g1zwi">
              <ChevronDown className={`h-3.5 w-3.5 transition ${showExtracted ? 'rotate-180' : ''}`} data-id="element-1751" data-mp="wn1ip53kxyr7" />
              Extracted data
            </button>
            {showExtracted && <ul className="divide-y divide-[#edf0eb]" data-id="element-1752" data-mp="16efnnseavqrq">
                {gmpExtracted.map(row => <li key={row.field} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5" data-id="element-1753" data-mp="1kce0gtcz3f54">
                    <span className="min-w-[10rem] font-mono text-[11px] font-semibold uppercase tracking-[0.11em] text-[#7a897d]" data-id="element-1754" data-mp="1gd59fw9bhvd9">{row.field}</span>
                    <span className="flex-1 text-sm text-evergreen" data-id="element-1755" data-mp="3i36wxiox2wzq">{row.value}</span>
                    <ConfidenceTag level="HIGH" data-id="element-1756" data-mp="3i59tklkkrodf" />
                  </li>)}
              </ul>}
          </div>
        </div>
      </div>
      <div className="shrink-0 lg:pl-4" data-id="element-1757" data-mp="1q2fauqcn6cly">
        <div className="flex flex-wrap justify-end gap-2" data-id="element-1758" data-mp="1q3gyrveratl3">
          <ResultAction icon={<Upload className="h-3.5 w-3.5" data-id="element-1760" data-mp="1uzfiv6t1xpfp" />} data-id="element-1759" data-mp="1uzxo2sxozdzr">Replace</ResultAction>
          <ResultAction icon={<Link2 className="h-3.5 w-3.5" data-id="element-1762" data-mp="201emmhspeod6" />} data-id="element-1761" data-mp="200v1ytbo5lvg">Reuse another</ResultAction>
          <ResultAction icon={<div className="h-3.5 w-3.5" data-id="element-1764" data-mp="240nflkfx6i7o" />} data-id="element-1763" data-mp="2426v38tr4p8e">Document history</ResultAction>
          <ResultAction destructive icon={<Trash2 className="h-3.5 w-3.5" data-id="element-1766" data-mp="28yxcq0l7fsvx" />} data-id="element-1765" data-mp="28zqa4ay9x61e">
            Delete
          </ResultAction>
        </div>
      </div>
    </article>;
}
function ResultAction({
  children,
  icon,
  destructive = false,
  onClick
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  destructive?: boolean;
  onClick?: () => void;
}) {
  return <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition ${destructive ? 'border-red-200 text-red-800 hover:bg-red-50' : 'border-[#d6dfd3] text-evergreen hover:bg-[#f7faf5]'}`} data-id="element-1767" data-mp="2nnl5y07kvqip">
      {icon}
      {children}
    </button>;
}
function StatLine({
  label,
  value,
  dot
}: {
  label: string;
  value: string;
  dot?: string;
}) {
  return <div className="flex items-center justify-between gap-3" data-id="element-1768" data-mp="25fvtlxdbhl5">
      <span className="inline-flex items-center gap-1.5" data-id="element-1769" data-mp="zs52i930midc">
        {dot && <span className={`h-2 w-2 rounded-full ${dot}`} data-id="element-1770" data-mp="ztg90gnui485" />}
        {label}
      </span>
      <span className="font-semibold text-evergreen" data-id="element-1771" data-mp="uun5nh29vtlp">{value}</span>
    </div>;
}
function RequirementRow({
  requirement,
  onUpload,
  onReuse
}: {
  requirement: Requirement;
  onUpload?: () => void;
  onReuse?: () => void;
}) {
  const [showWhy, setShowWhy] = useState(false);
  return <article className="flex flex-col gap-3 border-b border-[#edf0eb] py-5 last:border-b-0 lg:flex-row lg:items-start" data-id="element-1772" data-mp="1ijpkyi9fpad1">
      <div className="flex min-w-0 flex-1 gap-3" data-id="element-1773" data-mp="1dpbm25vkcl3m">
        <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full border-2 border-red-500" aria-hidden="true" data-id="element-1774" data-mp="m3l8e2jyt6df" />
        <div className="min-w-0 flex-1" data-id="element-1775" data-mp="qvlfceygczji">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1776" data-mp="qwmq0ibk3k0p">
            <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1777" data-mp="d4gc72vvlp4f">{requirement.name}</h3>
            <span className="rounded-full border border-red-300 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-red-700" data-id="element-1778" data-mp="h5jpbhmjh71y">
              Missing
            </span>
          </div>
          {requirement.greenNote && <p className="mt-2 inline-flex rounded-lg border border-forest-green/30 bg-forest-green/10 px-3 py-1.5 text-xs font-semibold text-forest-green" data-id="element-1779" data-mp="vtwr4v8b5k4d">
              {requirement.greenNote}
            </p>}
          <div className="mt-3 max-w-2xl" data-id="element-1780" data-mp="3gjm3n1ljgttw">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#819083]" data-id="element-1781" data-mp="3gikjm7mjox01">What to provide</p>
            <p className="mt-1 text-sm leading-relaxed text-[#687a6d]" data-id="element-1782" data-mp="3d1y1oe6ssh7n">{requirement.provided}</p>
          </div>
          <button type="button" onClick={() => setShowWhy(visible => !visible)} aria-expanded={showWhy} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#637667] hover:text-forest-green hover:underline" data-id="element-1783" data-mp="390mjuuu610a0">
            <ChevronDown className={`h-3.5 w-3.5 transition ${showWhy ? 'rotate-180' : ''}`} data-id="element-1784" data-mp="2ub6y5t9hrl16" />
            Why this document matters
          </button>
          {showWhy && <p className="mt-2 max-w-2xl rounded-lg bg-[#f4f7f2] p-3 text-sm leading-relaxed text-[#596c5e]" data-id="element-1785" data-mp="2pe6q5ct3087f">{requirement.why}</p>}
        </div>
      </div>
      <div className="shrink-0 lg:pl-4" data-id="element-1786" data-mp="2za1hqxfa9dmc">
        <div className="flex flex-wrap justify-end gap-2" data-id="element-1787" data-mp="2z98g2av6leb2">
          <button type="button" onClick={onUpload} className="inline-flex items-center gap-2 rounded-xl bg-evergreen px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-forest-green" data-id="element-1788" data-mp="2bk73qcj89j0i">
            <Upload className="h-4 w-4" data-id="element-1789" data-mp="17t2e0irn8p88" />
            Upload
          </button>
          {onReuse && <button type="button" onClick={onReuse} className="inline-flex items-center gap-2 rounded-xl border border-[#d6dfd3] bg-white px-4 py-2.5 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-1790" data-mp="12xwpdc6uwwgg">
              <Link2 className="h-4 w-4" data-id="element-1791" data-mp="o7fqo0llwkn9" />
              Reuse from Policies
            </button>}
        </div>
      </div>
    </article>;
}